import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  CalendarClock,
  Code as CodeIcon,
  Copy,
  FileText,
  FolderOpen,
  Mail as MailIcon,
  PenSquare,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateMail,
  useCreateMailProject,
  useDeleteMailProject,
  useDuplicateMail,
  useHardDeleteMail,
  useMailProjects,
  useMails,
  useRestoreMail,
  useSoftDeleteMail,
  useUpdateMail,
  type MailProjectRecord,
  type MailRecord,
} from '@/hooks/useMailData';
import { useMailSchedules } from '@/hooks/useMailSchedules';
import { useMailStaff } from '@/hooks/useMailSettings';
import { db, functions } from '@/lib/firebase';
import { formatDate, formatDateTime } from '@/lib/firestoreTime';
import { toHtml, toPlainText } from '@/lib/mailRender';
import { cn } from '@/lib/utils';
import { ScheduleFormDialog } from '@/pages/mail/schedules/ScheduleFormDialog';

import { ProjectDetailView } from './ProjectDetailView';

type Status = 'draft' | 'review' | 'approved' | 'sent';

function statusBadgeVariant(status: string) {
  return status === 'sent'
    ? 'sent'
    : status === 'approved'
      ? 'approved'
      : status === 'review'
        ? 'review'
        : 'draft';
}

// ─────────────────────────────────────────────
// リストビュー
// ─────────────────────────────────────────────
function MailListView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const { data, isLoading } = useMails({
    deleted: false,
    status: statusFilter || undefined,
    limit: 200,
  });
  const softDeleteMutation = useSoftDeleteMail();
  const createMailMutation = useCreateMail();
  const duplicateMailMutation = useDuplicateMail();
  const [creatingBlank, setCreatingBlank] = useState(false);

  // メールクリックで生成画面へ遷移 (v3.3 UX: ダイアログ廃止)
  const handleOpenInGenerate = (m: MailRecord) => {
    sessionStorage.setItem(
      'mail_restore',
      JSON.stringify({
        mailId: m.id,
        templateId: m.templateId ?? '',
        subject: m.subject ?? '',
        sections: m.sections ?? [],
        overview: m.overview ?? '',
        field_comments: m.field_comments ?? {},
        formData: m.formData ?? {},
        lang: m.lang ?? 'ja',
        modelUsed: m.modelUsed,
      }),
    );
    navigate('/mail/generate');
  };

  const handleSoftDelete = async (id: string, subject: string) => {
    if (!window.confirm(`「${subject}」をゴミ箱へ移動します。`)) return;
    try {
      await softDeleteMutation.mutateAsync(id);
      toast.success('ゴミ箱に移動しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // 一覧から直接複製（A/B テスト用途、spec B-14.2.1）
  const handleDuplicate = async (m: MailRecord) => {
    try {
      const newId = await duplicateMailMutation.mutateAsync(m.id);
      toast.success('メールを複製しました。生成画面で編集してください。');
      const dupSnap = await getDoc(doc(db, 'mails', newId));
      if (dupSnap.exists()) {
        const data = dupSnap.data();
        sessionStorage.setItem(
          'mail_restore',
          JSON.stringify({
            mailId: newId,
            templateId: data.templateId ?? '',
            subject: data.subject ?? '',
            sections: data.sections ?? [],
            overview: data.overview ?? '',
            field_comments: data.field_comments ?? {},
            formData: data.formData ?? {},
            lang: data.lang ?? 'ja',
            modelUsed: data.modelUsed,
          }),
        );
      }
      navigate('/mail/generate');
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // 空のメールを作成 → 生成画面で開く（手動編集用、Phase 22a / spec B-14.2.2）
  const handleCreateBlank = async () => {
    setCreatingBlank(true);
    try {
      const id = await createMailMutation.mutateAsync({
        subject: '無題',
        sections: [],
        overview: '',
        field_comments: {},
        templateId: '',
        formData: {},
        lang: 'ja',
        modelUsed: '',
        sectionComments: {},
        note: '',
        status: 'draft',
        projectIds: [],
      });
      sessionStorage.setItem(
        'mail_restore',
        JSON.stringify({
          mailId: id,
          templateId: '',
          subject: '無題',
          sections: [],
          overview: '',
          field_comments: {},
          formData: {},
          lang: 'ja',
        }),
      );
      navigate('/mail/generate');
    } catch (err) {
      toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCreatingBlank(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Label className="!mb-0 !text-xs">ステータス:</Label>
        <div className="flex gap-1">
          {(['', 'draft', 'review', 'approved', 'sent'] as const).map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setStatusFilter(s as Status | '')}
              className={cn(
                'px-3 py-1 rounded-sm text-xs border transition-colors',
                statusFilter === s
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
              )}
            >
              {s === '' ? 'すべて' : s}
            </button>
          ))}
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="ml-auto"
            onClick={handleCreateBlank}
            disabled={creatingBlank}
          >
            <Plus />
            {creatingBlank ? '作成中…' : '新規メール（空白）'}
          </Button>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : data && data.length > 0 ? (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-4 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              onClick={() => handleOpenInGenerate(m)}
            >
              <MailIcon className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-[13px] truncate">
                    {m.subject || '(件名なし)'}
                  </span>
                  <Badge variant={statusBadgeVariant(m.status)}>{m.status}</Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
                  <span>{formatDateTime(m.createdAt)}</span>
                  <span>{m.sections?.length ?? 0} セクション</span>
                </div>
              </div>
              {canEdit && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDuplicate(m);
                    }}
                    disabled={duplicateMailMutation.isPending}
                    title="複製"
                  >
                    <Copy />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleSoftDelete(m.id, m.subject || m.id);
                    }}
                    title="ゴミ箱へ"
                  >
                    <Trash2 />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={MailIcon}
          title="メールがありません"
          description={
            statusFilter
              ? `ステータス ${statusFilter} のメールはありません`
              : 'メール生成画面で作成してください'
          }
        />
      )}

    </div>
  );
}

// ─────────────────────────────────────────────
// プロジェクトビュー
// ─────────────────────────────────────────────
function ProjectsView() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data: projects, isLoading } = useMailProjects();
  const createMutation = useCreateMailProject();
  const deleteMutation = useDeleteMailProject();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#4a7cff');
  const [openProject, setOpenProject] = useState<MailProjectRecord | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      await createMutation.mutateAsync({ name: name.trim(), color });
      toast.success('プロジェクトを作成しました');
      setCreating(false);
      setName('');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (id: string, n: string) => {
    if (!window.confirm(`プロジェクト「${n}」を削除します。メール自体は削除されません。`)) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // プロジェクト詳細ビュー表示中は一覧を隠す
  if (openProject) {
    return <ProjectDetailView project={openProject} onBack={() => setOpenProject(null)} />;
  }

  return (
    <div>
      {canEdit && (
        <div className="flex justify-end mb-4">
          <Button type="button" variant="primary" onClick={() => setCreating(true)}>
            <Plus />
            プロジェクト作成
          </Button>
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div
              key={p.id}
              className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4 relative overflow-hidden cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
              onClick={() => setOpenProject(p)}
            >
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: p.color }} />
              <div className="flex items-start gap-2 mb-3">
                <div
                  className="size-3 rounded-full mt-1 flex-shrink-0"
                  style={{ background: p.color }}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{p.name}</h3>
                  <p className="text-xs text-[var(--text-muted)]">{formatDate(p.createdAt)}</p>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">
                    クリックして詳細を開く
                  </p>
                </div>
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(p.id, p.name);
                    }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={FolderOpen}
          title="プロジェクトがありません"
          description="キャンペーン単位でメールをグルーピングできます"
        />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>プロジェクトを作成</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>プロジェクト名</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="2026 年スプリングセール"
              />
            </div>
            <div>
              <Label>カラー</Label>
              <div className="flex gap-2">
                {['#4a7cff', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#22d3ee'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={cn(
                      'size-8 rounded-full border-2',
                      color === c ? 'border-[var(--text-primary)]' : 'border-transparent',
                    )}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
              キャンセル
            </Button>
            <Button type="button" variant="primary" onClick={handleCreate} disabled={!name.trim()}>
              作成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// ─────────────────────────────────────────────
// ゴミ箱ビュー
// ─────────────────────────────────────────────
function TrashView() {
  const { data, isLoading } = useMails({ deleted: true, limit: 100 });
  const restoreMutation = useRestoreMail();
  const hardDeleteMutation = useHardDeleteMail();

  const handleRestore = async (id: string) => {
    try {
      await restoreMutation.mutateAsync(id);
      toast.success('復元しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleHardDelete = async (id: string, subject: string) => {
    // 関連 mail_schedules を事前カウント（未送信が含まれる場合は警告強化）
    let relatedTotal = 0;
    let activeCount = 0;
    try {
      const snap = await getDocs(
        query(collection(db, 'mail_schedules'), where('mailId', '==', id)),
      );
      relatedTotal = snap.size;
      for (const d of snap.docs) {
        const status = (d.data() as { status?: string }).status;
        if (status === 'scheduled' || status === 'sending') activeCount++;
      }
    } catch {
      // クエリ失敗時は件数なしで進める（Trigger 側でも整合は取れる）
    }

    let confirmMsg = `「${subject}」を完全削除します。この操作は取り消せません。`;
    if (relatedTotal > 0) {
      confirmMsg += `\n\n関連する配信予約 ${relatedTotal} 件（うち未送信 ${activeCount} 件）も同時に削除されます。`;
      if (activeCount > 0) {
        confirmMsg += `\n⚠ 未送信の予約がキャンセルされます。本当に削除してよろしいですか？`;
      }
    }
    if (!window.confirm(confirmMsg)) return;
    try {
      await hardDeleteMutation.mutateAsync(id);
      toast.success(
        relatedTotal > 0
          ? `完全削除しました（関連予約 ${relatedTotal} 件も自動削除）`
          : '完全削除しました',
      );
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">
        ソフト削除されたメールは保持期間経過後に自動削除されます（デフォルト 30 日）
      </p>
      {isLoading ? (
        <Spinner />
      ) : data && data.length > 0 ? (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0"
            >
              <Trash2 className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium truncate">{m.subject || '(件名なし)'}</div>
                <div className="text-xs text-[var(--text-muted)]">
                  削除: {formatDateTime(m._deletedAt)}
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => handleRestore(m.id)}>
                <RotateCcw />
                復元
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => handleHardDelete(m.id, m.subject || m.id)}
              >
                <Trash2 />
                完全削除
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Trash2} title="ゴミ箱は空です" />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Mail 詳細ダイアログ（プレビュー + 送信）
// 仕様書: docs/01_spec/spec_mail_system.md B-13（Phase 21: 本番送信ボタン廃止 / 配信予約導入）
// ─────────────────────────────────────────────
const sendTestMailFn = httpsCallable<
  { mailId: string; ctaUrl?: string },
  { ok: boolean; messageId: string; to: string }
>(functions, 'sendTestMail');

const requestMailReviewFn = httpsCallable<
  { mailId: string; staff: { name: string; email: string }; comment: string },
  { ok: boolean; messageId: string }
>(functions, 'requestMailReview');

export function MailDetailDialog({ mail, onClose }: { mail: MailRecord; onClose: () => void }) {
  const navigate = useNavigate();
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerComment, setReviewerComment] = useState('');
  const [sending, setSending] = useState<'test' | 'review' | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [viewMode, setViewMode] = useState<'text' | 'html'>('text');
  const { data: staff } = useMailStaff();
  const { data: allProjects } = useMailProjects();
  const updateMailMutation = useUpdateMail();
  const duplicateMutation = useDuplicateMail();
  const [assignedProjectIds, setAssignedProjectIds] = useState<string[]>(mail.projectIds ?? []);

  // この mail の予約件数（リンク表示用）
  const { data: schedules } = useMailSchedules({ mailId: mail.id });
  const activeSchedules = (schedules ?? []).filter(
    (s) => s.status === 'scheduled' || s.status === 'sending',
  );

  const toggleProject = async (projectId: string) => {
    const current = new Set(assignedProjectIds);
    if (current.has(projectId)) current.delete(projectId);
    else current.add(projectId);
    const next = Array.from(current);
    setAssignedProjectIds(next);
    try {
      await updateMailMutation.mutateAsync({ id: mail.id, data: { projectIds: next } });
    } catch (err) {
      toast.error(`プロジェクト更新失敗: ${err instanceof Error ? err.message : String(err)}`);
      // Revert on error
      setAssignedProjectIds(mail.projectIds ?? []);
    }
  };

  const handleTest = async () => {
    setSending('test');
    try {
      const res = await sendTestMailFn({ mailId: mail.id });
      toast.success(`テスト送信: ${res.data.to}`);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(null);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const newId = await duplicateMutation.mutateAsync(mail.id);
      toast.success('メールを複製しました。生成画面で編集してください。');
      // 複製先を生成画面で開くため restore state を設定
      const dupSnap = await getDoc(doc(db, 'mails', newId));
      if (dupSnap.exists()) {
        const data = dupSnap.data();
        sessionStorage.setItem(
          'mail_restore',
          JSON.stringify({
            mailId: newId,
            templateId: data.templateId ?? '',
            subject: data.subject ?? '',
            sections: data.sections ?? [],
            overview: data.overview ?? '',
            field_comments: data.field_comments ?? {},
            formData: data.formData ?? {},
            lang: data.lang ?? 'ja',
            modelUsed: data.modelUsed,
          }),
        );
      }
      onClose();
      navigate('/mail/generate');
    } catch (err) {
      toast.error(`複製失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDuplicating(false);
    }
  };

  const handleReview = async () => {
    if (!reviewerEmail) {
      toast.error('レビュー担当者を選択してください');
      return;
    }
    setSending('review');
    try {
      await requestMailReviewFn({
        mailId: mail.id,
        staff: { name: reviewerName, email: reviewerEmail },
        comment: reviewerComment,
      });
      toast.success('レビュー依頼を送信しました');
      setReviewerEmail('');
      setReviewerName('');
      setReviewerComment('');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(null);
    }
  };

  const handleEditInGenerate = () => {
    const restore = {
      mailId: mail.id,
      templateId: mail.templateId,
      subject: mail.subject,
      sections: mail.sections ?? [],
      overview: mail.overview ?? '',
      field_comments: mail.field_comments ?? {},
      formData: mail.formData ?? {},
      lang: mail.lang ?? 'ja',
      modelUsed: mail.modelUsed,
    };
    sessionStorage.setItem('mail_restore', JSON.stringify(restore));
    navigate('/mail/generate');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[min(96vw,1200px)] max-h-[92vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <DialogTitle className="flex-1 truncate">{mail.subject}</DialogTitle>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleDuplicate}
              disabled={duplicating || sending !== null}
              title="A/B テスト用などにコピーを作成"
            >
              <Copy />
              {duplicating ? '複製中…' : '複製'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleEditInGenerate}
              title="メール生成画面で編集"
            >
              <PenSquare />
              生成画面で編集
            </Button>
          </div>
        </DialogHeader>

        {/* 2 カラム本体 */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] flex-1 min-h-0 overflow-hidden">
          {/* 左: 設定パネル */}
          <div className="overflow-y-auto p-6 space-y-5 border-r border-[var(--border)] bg-[var(--bg-surface)]">
            <div>
              <h4 className="text-xs font-semibold uppercase mb-2 text-[var(--text-secondary)]">
                テスト送信
              </h4>
              <p className="text-[11px] text-[var(--text-muted)] mb-2 leading-relaxed">
                自分宛に試送します。CTA URL はメール本文（CTA セクション）の設定値が使われます。
              </p>
              <Button
                type="button"
                variant="secondary"
                className="w-full mt-2"
                onClick={handleTest}
                disabled={sending !== null || duplicating}
              >
                {sending === 'test' ? '送信中...' : '自分宛にテスト送信'}
              </Button>
            </div>

            <div className="border-t border-[var(--border)] pt-4">
              <h4 className="text-xs font-semibold uppercase mb-2 text-[var(--text-secondary)]">
                配信予約
              </h4>
              <p className="text-[11px] text-[var(--text-muted)] mb-2 leading-relaxed">
                送信先（タグ / グループ / 直接アドレス）と送信日時を指定して予約します。
                予約時刻に自動送信されます。
              </p>
              <Button
                type="button"
                variant="primary"
                className="w-full mt-2"
                onClick={() => setScheduleOpen(true)}
                disabled={sending !== null}
              >
                <CalendarClock />
                配信予約
              </Button>
              {activeSchedules.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/mail/schedules?mailId=${mail.id}`);
                  }}
                  className="block mt-2 text-[11px] text-[var(--accent)] hover:underline w-full text-center"
                >
                  予約済 {activeSchedules.length} 件 →
                </button>
              )}
            </div>

            <div className="border-t border-[var(--border)] pt-4">
              <h4 className="text-xs font-semibold uppercase mb-2 text-[var(--text-secondary)]">
                プロジェクト
              </h4>
              {!allProjects || allProjects.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic">
                  プロジェクトが未登録です（プロジェクトタブから作成）
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {allProjects.map((p) => {
                    const active = assignedProjectIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleProject(p.id)}
                        className={cn(
                          'px-2.5 py-1 rounded-sm text-xs border flex items-center gap-1.5 transition-colors',
                          active
                            ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                            : 'border-[var(--border)] hover:bg-[var(--bg-hover)]',
                        )}
                      >
                        <span
                          className="size-2 rounded-full"
                          style={{ background: p.color }}
                        />
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="border-t border-[var(--border)] pt-4">
              <h4 className="text-xs font-semibold uppercase mb-2 text-[var(--text-secondary)]">
                レビュー依頼
              </h4>
              <div className="grid grid-cols-1 gap-2 mb-2">
                <div>
                  <Label>担当者</Label>
                  <select
                    className="w-full h-[34px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-2 text-[13px]"
                    value={reviewerEmail}
                    onChange={(e) => {
                      const s = staff?.list?.find((x) => x.email === e.target.value);
                      setReviewerEmail(e.target.value);
                      setReviewerName(s?.name ?? '');
                    }}
                  >
                    <option value="">-- 選択 --</option>
                    {staff?.list?.map((s) => (
                      <option key={s.email} value={s.email}>
                        {s.name} &lt;{s.email}&gt;
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>担当者名（自由記入可）</Label>
                  <Input value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} />
                </div>
              </div>
              <Label>コメント</Label>
              <Textarea
                rows={2}
                value={reviewerComment}
                onChange={(e) => setReviewerComment(e.target.value)}
                placeholder="レビュアーに伝えたい内容"
              />
              <Button
                type="button"
                variant="secondary"
                className="w-full mt-2"
                onClick={handleReview}
                disabled={sending !== null || !reviewerEmail}
              >
                {sending === 'review' ? '送信中...' : 'レビュー依頼を送信'}
              </Button>
            </div>
          </div>

          {/* 右: メールプレビュー（テキスト / HTML 切替） */}
          <div className="flex flex-col overflow-hidden bg-[var(--bg-elevated)]">
            <div className="px-6 py-3 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
              <div className="flex rounded border border-[var(--border)] overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setViewMode('text')}
                  className={cn(
                    'px-3 py-1.5 flex items-center gap-1.5 transition-colors',
                    viewMode === 'text'
                      ? 'bg-[var(--accent)] text-white'
                      : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]',
                  )}
                >
                  <FileText className="size-3" />
                  テキスト
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('html')}
                  className={cn(
                    'px-3 py-1.5 flex items-center gap-1.5 transition-colors',
                    viewMode === 'html'
                      ? 'bg-[var(--accent)] text-white'
                      : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]',
                  )}
                >
                  <CodeIcon className="size-3" />
                  HTML
                </button>
              </div>
              <span className="text-[11px] text-[var(--text-muted)]">
                CTA URL を含むプレビュー（実送信メールと同じ本文）
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-3">
                <div className="text-[11px] text-[var(--text-muted)] uppercase mb-1">件名</div>
                <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] px-3 py-2 text-[14px] font-semibold">
                  {mail.subject}
                </div>
              </div>
              {viewMode === 'text' ? (
                <div className="bg-white text-black p-5 rounded border border-[var(--border)] text-[13px] whitespace-pre-wrap leading-relaxed min-h-[300px]">
                  {toPlainText({
                    subject: mail.subject,
                    sections: mail.sections ?? [],
                  })}
                </div>
              ) : (
                <iframe
                  title="HTML preview"
                  srcDoc={toHtml({
                    subject: mail.subject,
                    sections: mail.sections ?? [],
                  })}
                  className="w-full min-h-[500px] rounded border border-[var(--border)] bg-white"
                  sandbox=""
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[var(--border)]">
          <Button type="button" variant="secondary" onClick={onClose}>
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
      <ScheduleFormDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        prefillMailId={mail.id}
        onScheduled={() => {
          // 予約成功 → ダイアログ閉じる（spec B-13.1）
          onClose();
        }}
      />
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// トップレベル
// ─────────────────────────────────────────────
export function MailListPage() {
  return (
    <Screen title="メールリスト" description="メール一覧 / プロジェクト / ゴミ箱">
      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">リスト</TabsTrigger>
          <TabsTrigger value="projects">プロジェクト</TabsTrigger>
          <TabsTrigger value="trash">ゴミ箱</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
          <MailListView />
        </TabsContent>
        <TabsContent value="projects">
          <ProjectsView />
        </TabsContent>
        <TabsContent value="trash">
          <TrashView />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
